/**
 * OaSIS Explorer Frontend - localStorage Utilities
 * Task 3.1: Frontend Foundation & Global Features
 * 
 * Safe localStorage operations with error handling
 */

import type { Language, Theme } from '../types';

// localStorage keys
export const STORAGE_KEYS = {
  LANGUAGE: 'oasis-language',
  THEME: 'oasis-theme',
} as const;

/**
 * Safe localStorage getter with fallback
 */
const safeGetItem = (key: string): string | null => {
  try {
    return localStorage.getItem(key);
  } catch (error) {
    console.warn(`Failed to read from localStorage for key "${key}":`, error);
    return null;
  }
};

/**
 * Safe localStorage setter with error handling
 */
const safeSetItem = (key: string, value: string): boolean => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (error) {
    console.warn(`Failed to write to localStorage for key "${key}":`, error);
    return false;
  }
};

/**
 * Language preference management
 */
export const languageStorage = {
  get: (): Language => {
    const stored = safeGetItem(STORAGE_KEYS.LANGUAGE);
    
    // Validate stored value
    if (stored === 'en' || stored === 'fr') {
      return stored;
    }
    
    // Detect browser language preference
    const browserLang = navigator.language.toLowerCase();
    if (browserLang.startsWith('fr')) {
      return 'fr';
    }
    
    // Default to English
    return 'en';
  },
  
  set: (language: Language): boolean => {
    return safeSetItem(STORAGE_KEYS.LANGUAGE, language);
  },
  
  clear: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEYS.LANGUAGE);
    } catch (error) {
      console.warn('Failed to clear language from localStorage:', error);
    }
  },
};

/**
 * Theme preference management
 */
export const themeStorage = {
  get: (): Theme => {
    const stored = safeGetItem(STORAGE_KEYS.THEME);
    
    // Validate stored value
    if (stored === 'light' || stored === 'dark') {
      return stored;
    }
    
    // Detect system theme preference
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    
    // Default to light theme
    return 'light';
  },
  
  set: (theme: Theme): boolean => {
    return safeSetItem(STORAGE_KEYS.THEME, theme);
  },
  
  clear: (): void => {
    try {
      localStorage.removeItem(STORAGE_KEYS.THEME);
    } catch (error) {
      console.warn('Failed to clear theme from localStorage:', error);
    }
  },
};

/**
 * System theme detection utilities
 */
export const systemTheme = {
  /**
   * Get current system theme preference
   */
  get: (): Theme => {
    if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      return 'dark';
    }
    return 'light';
  },
  
  /**
   * Listen for system theme changes
   */
  onChange: (callback: (theme: Theme) => void): (() => void) => {
    if (!window.matchMedia) {
      return () => {}; // No-op cleanup function
    }
    
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handler = (e: MediaQueryListEvent) => {
      callback(e.matches ? 'dark' : 'light');
    };
    
    // Use addEventListener if available (modern browsers)
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handler);
      return () => mediaQuery.removeEventListener('change', handler);
    }
    
    // Fallback for older browsers
    if (mediaQuery.addListener) {
      mediaQuery.addListener(handler);
      return () => mediaQuery.removeListener(handler);
    }
    
    return () => {}; // No-op cleanup function
  },
};

